var $ = require('jquery');
var _ = require('underscore');
var paymentDetailsUtils = require('./paymentDetailsUtils');
var handlebars = require('hbsfy/runtime');
var TorsoFormModel = require('backbone-torso/modules/FormModel');
var billingConfigModel = require('./../financial/billingConfigModel');
var userService = require('./../user/userService');
var PaymentTransactionModel = require('./PaymentTransactionModel');
var dateUtils = require('./../utils/dateUtils');

var DEFAULT_PATTERN = '{{9999}} {{9999}} {{9999}} {{9999}}';
var NUMBERS_ONLY = /^[0-9]+$/;
var ANY_NOT_NUMBERS = /[^0-9]+/;

var PAYMENT_DETAILS_TYPE = {
  OUTSTANDING_BILL: 'OUTSTANDING_BILL',
  INSURANCE_ESTIMATION: 'INSURANCE_ESTIMATION',
  APPOINTMENT_ESTIMATE: 'APPOINTMENT_ESTIMATE'
};

var PAYMENT_METHODS = {
  creditCard: 'creditCard',
  ach: 'ach',
  paymentProcessingDevice: 'paymentProcessingDevice',
  paymentDataDevice: 'paymentDataDevice',
  isStoredPaymentMethod: function(value) {
    var vecnaIdRegex = '[A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-4[A-Fa-f0-9]{3}-[89ABab][A-Fa-f0-9]{3}-[A-Fa-f0-9]{12}';
    return new RegExp(vecnaIdRegex).test(value);
  }
};

var CARDS = {
  visa: {
    name: 'Visa',
    regex: /^4/,
    securityCodeSize: 3,
    lengths: [13, 16],
    pattern: DEFAULT_PATTERN
  },
  mastercard: {
    name: 'MasterCard',
    regex: /^5[1-5]/,
    securityCodeSize: 3,
    lengths: [16],
    pattern: DEFAULT_PATTERN
  },
  amex: {
    name: 'Amex',
    regex: /^3[47]/,
    securityCodeSize: 4,
    lengths: [15],
    pattern: '{{9999}} {{999999}} {{99999}}'
  },
  discover: {
    name: 'Discover',
    regex: /^6(?:011|5[0-9]{2})/,
    securityCodeSize: 3,
    lengths: [16],
    pattern: DEFAULT_PATTERN
  },
  unknown: {
    name: 'Unknown',
    regex: /.*/,
    securityCodeSize: 3,
    lengths: [16],
    pattern: DEFAULT_PATTERN
  }
};

/**
 * The PaymentDetailsFormModel is a form model that contains the validation rules for payments
 * @class PaymentDetailsFormModel
 * @constructor
 * @extends Torso.FormModel
 */
var PaymentDetailsFormModel = TorsoFormModel.extend({
  /**
   * The validation rules for this form model
   * @property validation
   */
  validation: {
    /**
     * Ensure that the total payment is greater than zero.
     * @property validation.noPayment {validation entry}
     */
    'noPayment': {
      fn: function() {
        if (!this._isMakingPayment) {
          return '';
        }
        return this.getTotalPaymentAmount() >= 0.01 ? '' : 'error.zero';
      }
    },
    /**
     * Makes sure that the payment amount is <= account balance.
     * @property validation['billPaymentDetails[]'] {validation entry}
     */
    'billPaymentDetails': {
      fn: function(billPaymentDetails) {
        if (!this._isMakingPayment) {
          return '';
        }
        var thisPaymentDetailsFormModel = this;
        var billMessageCollection = this._billMessageCollection;
        var insuranceEstimationCollection = this._insuranceEstimationCollection;
        var errors = _.mapObject(billPaymentDetails, function(amount, identifier) {
          var billMessageVecnaIdOrAccountNumber = thisPaymentDetailsFormModel.getBillMessageVecnaIdOrAccountNumberFromIdentifier(identifier);
          var billModel = billMessageCollection.getMessageByVecnaId(billMessageVecnaIdOrAccountNumber);
          var estimateModel = insuranceEstimationCollection && insuranceEstimationCollection.getByAccountNumber(billMessageVecnaIdOrAccountNumber);

          var amountDue = 0;
          var error = '';
          if (billModel) {
            amountDue = billModel.get('billDTO.externalBill.accountBalance');
          } else if (estimateModel) {
            amountDue = estimateModel.get('remainingBalance');
          } else {
            error = 'error.identifier.notFound';
          }

          if (!error && billPaymentDetails[identifier].amount > amountDue) {
            error = 'error.overpay';
          }
          return {
            error: error,
            paymentIdentifier: identifier
          };
        });
        errors = _.pick(errors, function(error) {
          return error.error;
        });
        if (!_.isEmpty(errors)) {
          return errors;
        }
      }
    },
    /**
     * Required validation for the method of payment
     * @property validation.paymentMethod {validation entry}
     */
    'paymentMethod': {
      requiredField: 'true'
    },
    /**
     * Required validation for the name on card field
     * @property validation.accountOwnerName {validation entry}
     */
    'accountOwnerName': {
      requiredField: {
        shouldValidate: '_shouldValidateCardOrAchData'
      },
      fn: function(accountOwnerName) {
        if (!this._shouldValidateCardOrAchData() || !accountOwnerName) {
          return '';
        }
        accountOwnerName = $.trim(accountOwnerName);
        if (NUMBERS_ONLY.test(accountOwnerName)) {
          return {
            key: 'error.digits.only'
          };
        } else if (accountOwnerName.length <= 1) {
          return {
            key: 'error.length'
          };
        }
      }
    },
    /**
     * Require that the payment device id is set if the payment method is paymentProcessingDevice.
     * @property validation['paymentDeviceId'] {validation entry}
     */
    'paymentDeviceId': {
      requiredField: {
        shouldValidate: '_shouldValidatePaymentDevice'
      }
    },
    /**
     * Required validation for the number on card field, as well as a check to make sure the
     * number is a valid credit card number as defined by the Luhn Algorithm, and has the correct
     * number of digits, and matches a known credit card pattern.
     * @property validation['cardData.cardNumber'] {validation entry}
     */
    'cardData.cardNumber': {
      requiredField: {
        shouldValidate: '_shouldValidateSensitiveCardData'
      },
      fn: function(cardNumber) {
        if (!this._shouldValidateSensitiveCardData()) {
          return '';
        }
        cardNumber = cardNumber ? cardNumber.replace(/\D/g, '') : '';
        var cardTypeInfo = this.getCardTypeByNumber();
        var correctLengths = cardTypeInfo.lengths;
        if (!_.contains(correctLengths, cardNumber.length)) {
          return {
            key: 'error.length'
          };
        } else if (cardTypeInfo.name === this.CARDS.unknown.name) {
          return {
            key: 'error.unknown'
          };
        } else if (!paymentDetailsUtils.isValidCardNumber(cardNumber)) {
          return {
            key: 'error.number.wrong'
          };
        }
      }
    },
    /**
     * Ensure that the credit card expiration date is not in the past.
     * @property validation.expirationDateInPast {validation entry}
     */
    'expirationDateInPast': {
      fn: function() {
        var currentDate, currentMonth, selectedMonth, currentYear, selectedYear;
        currentDate = new Date();
        currentMonth = currentDate.getMonth() + 1; // Need to add 1 because getMonth() is zero-indexed (i.e., January = 0)
        currentYear = currentDate.getFullYear();
        selectedYear = parseInt(this.get('cardData.expirationYear'), 10);
        selectedMonth = parseInt(this.get('cardData.expirationMonth'), 10);
        if (selectedYear < currentYear || (selectedYear === currentYear && selectedMonth < currentMonth)) {
          return 'error.inPast';
        }
      }
    },
    /**
     * Required validation for the expiration month field
     * @property validation['cardData.expirationMonth'] {validation entry}
     */
    'cardData.expirationMonth': {
      requiredField: {
        shouldValidate: '_shouldValidateSensitiveCardData'
      }
    },
    /**
     * Required validation for the expiration year field
     * @property validation['cardData.expirationYear'] {validation entry}
     */
    'cardData.expirationYear': {
      requiredField: {
        shouldValidate: '_shouldValidateSensitiveCardData'
      }
    },
    /**
     * Validation for the credit card verification code field. The field is required,
     * and need to have the correct number of digits for the type of card and is numeric.
     * @property validation['cardData.securityCode'] {validation entry}
     */
    'cardData.securityCode': {
      requiredField: {
        shouldValidate: '_shouldValidateSensitiveCardData'
      },
      fn: function(securityCode) {
        if (!this._shouldValidateSensitiveCardData()) {
          return '';
        }
        // Ensure that the verification code has the correct length for the matching card type.
        var cardType = this.get('cardData.type');
        var cardTypeInfo = this.getCardTypeInfoByName(cardType);
        var correctNumDigits = cardTypeInfo.securityCodeSize;
        if (securityCode) {
          securityCode = $.trim(securityCode);
        }
        if (!securityCode || securityCode.length !== correctNumDigits) {
          return {
            key: 'error.numDigits',
            params: cardType
          };
        // Ensure that the verification code only has numeric characters.
        } else if (ANY_NOT_NUMBERS.test(securityCode)) {
          return 'error.nonNumeric';
        } else {
          return '';
        }
      }
    },
    /**
     * Required validation for the encrypted card information
     * @property validation['cardData.encryptedCardInfo'] {validation entry}
     */
    'cardData.encryptedCardInfo': {
      requiredField: {
        shouldValidate: '_shouldValidateCardData'
      },
      fn: function(value) {
        if (value === 'script.failed' || value === 'encryption.failed') {
          //just pass that error key through as-is
          return {
            key: value
          };
        }
      }
    },

    /**
     * Required validation for the ach routing number field
     * @property validation['achData.routingNumber'] {validation entry}
     */
    'achData.routingNumber': {
      requiredField: {
        shouldValidate: '_shouldValidateAchData'
      },
      fn: function(routing) {
        if (!this._shouldValidateAchData() || !routing) {
          return '';
        }
        routing = $.trim(routing);
        if (ANY_NOT_NUMBERS.test(routing)) {
          return {
            key: 'error.digits.only'
          };
        } else if (routing.length !== 9) {
          return {
            key: 'error.length'
          };
        } else if (!paymentDetailsUtils.isValidRoutingNumber(routing)) {
          return {
            key: 'error.number.wrong'
          };
        }
      }
    },
    /**
     * Required validation for the ach account number field
     * @property validation['achData.accountNumber'] {validation entry}
     */
    'achData.accountNumber': {
      requiredField: {
        shouldValidate: '_shouldValidateAchData'
      },
      fn: function(accountNumber) {
        if (!this._shouldValidateAchData() || !accountNumber) {
          return '';
        }
        accountNumber = $.trim(accountNumber);
        if (ANY_NOT_NUMBERS.test(accountNumber)) {
          return {
            key: 'error.digits.only'
          };
        } else {
          return '';
        }
      }
    },
    /**
     * Required validation for the ach account number field
     * @property validation['achData.accountNumber'] {validation entry}
     */
    'confirmAccountNumber': {
      requiredField: {
        shouldValidate: '_shouldValidateAchData'
      },
      fn: function(confirmedAccountNumber) {
        var achAccountNumber;
        if (!this._shouldValidateAchData() || !confirmedAccountNumber) {
          return '';
        }
        // Ensure that confirm account number matches the ach account number.
        confirmedAccountNumber = $.trim(confirmedAccountNumber);
        achAccountNumber = this.get('achData.accountNumber');
        if (achAccountNumber) {
          achAccountNumber = $.trim(achAccountNumber);
        }
        if (confirmedAccountNumber && confirmedAccountNumber !== achAccountNumber) {
          return {
            key: 'ach.accountNumber.confirm.match'
          };
        } else {
          return '';
        }
      }
    },
    /**
     * Required validation for the ach driver's license number field
     * @property validation['achData.driversLicenseNumber'] {validation entry}
     */
    'achData.driversLicenseNumber': {
      requiredField: {
        shouldValidate: '_shouldValidateAchData'
      }
    },
    /**
     * Required validation for the ach driver's license state field
     * @property validation['achData.driversLicenseState'] {validation entry}
     */
    'achData.driversLicenseState': {
      requiredField: {
        shouldValidate: '_shouldValidateAchData'
      }
    },

    /**
     * Required validation for the nickname for this payment method field.
     * @property validation.nickname {validation entry}
     */
    'storePayment.nickname': {
      requiredField: {
        shouldValidate: '_shouldValidateNickname'
      },
      fn: function(nickname) {
        if (!this._shouldValidateNickname() || !nickname) {
          return '';
        }
        nickname = $.trim(nickname);
        if (nickname.length <= 1) {
          return {
            key: 'error.too.short'
          };
        }
        if (nickname.length > 255) {
          return {
            key: 'error.too.long'
          };
        }
        var existingStoredPaymentNicknames = this._getExistingStoredPaymentNicknames();
        var nicknameExists = _.some(existingStoredPaymentNicknames, function(existingStoredPaymentNickname) {
          return existingStoredPaymentNickname === nickname;
        });
        if (nicknameExists) {
          return {
            key: 'error.duplicate'
          };
        }

      }
    }
  },

  mapping: {
    storedPaymentMethod: {
      storedPaymentMethod: false,
      pull: function(models) {
        this.set('cardHolderName', models.storedPaymentMethod.accountOwner);
        this.set('paymentMethod', models.storedPaymentMethod.accountType);
        this.set('storePayment.nickname', models.storedPaymentMethod.nickname);
        this.set('storePayment.token', models.storedPaymentMethod.token);
        this.set('storePayment.tokenProvider', models.storedPaymentMethod.tokenProvider);
        this.set('storePayment.vecnaId', models.storedPaymentMethod.vecnaId);
        this.set('storePayment.default', models.storedPaymentMethod.defaultPaymentMethod);

        var cardData = models.storedPaymentMethod.paymentData.card;
        if (cardData) {
          var yearMonthParts = cardData.expirationDate;
          var year = yearMonthParts && yearMonthParts[0];
          var month = yearMonthParts && yearMonthParts[1];
          this.set('cardData.expirationYear', year);
          this.set('cardData.expirationMonth', month);
          this.set('cardData.type', cardData.cardType);
        }
      },
      push: function(models) {
        models.storedPaymentMethod.set('patientIdentifiers', this.get('patientIdentifiers'));
        models.storedPaymentMethod.set('accountOwner', this.get('cardHolderName'));
        var paymentMethod = this.get('paymentMethod');
        models.storedPaymentMethod.set('accountType', paymentMethod);
        models.storedPaymentMethod.set('nickname', this.get('storePayment.nickname'));
        models.storedPaymentMethod.set('token', this.get('storePayment.token'));
        models.storedPaymentMethod.set('tokenProvider', this.get('storePayment.tokenProvider'));
        models.storedPaymentMethod.set('vecnaId', this.get('storePayment.vecnaId'));
        models.storedPaymentMethod.set('defaultPaymentMethod', this.get('storePayment.default'));

        if (paymentMethod === PAYMENT_METHODS.creditCard) {
          var cardNumberLast4 = this.calculateCreditCardNumberLast4();
          var card = {
            cardNumberLast4: cardNumberLast4,
            expirationDate: this.calculateExpirationYearMonth(),
            cardType: this.get('cardData.type'),
            encryptedCardInfo: this.get('cardData.encryptedCardInfo')
          };
          models.storedPaymentMethod.set('paymentData', {
            card: card
          });
        } else if (paymentMethod === PAYMENT_METHODS.ach) {
          var accountNumberLast4 = this.calculateAchAccountNumberLast4();
          var ach = {
            routingNumber: this.get('achData.routingNumber'),
            accountNumber: this.get('achData.accountNumber'),
            driversLicenseNumber: this.get('achData.driversLicenseNumber'),
            driversLicenseState: this.get('achData.driversLicenseState'),
            accountNumberLast4: accountNumberLast4
          };
          models.storedPaymentMethod.set('paymentData', {
            ach: ach
          });
        }
      }
    },

    paymentTransaction: {
      paymentTransaction: false,
      pull: function() {
        // do nothing
      },
      push: function(models) {
        var paymentMethod = this.get('paymentMethod');
        var paymentTransactionModel = models.paymentTransaction;
        paymentTransactionModel.set('patientIdentifiers', this.get('patientIdentifiers'));

        var currentBillPaymentDetails = this.getCurrentBillPaymentDetails();
        if (!_.isEmpty(currentBillPaymentDetails)) {
          var billInfo = this._getBillInfoForPaymentTransaction(currentBillPaymentDetails);
          paymentTransactionModel.set('billInfo', billInfo);
        }
        if (paymentMethod === PAYMENT_METHODS.paymentDataDevice) {
          var tabletDevicePaymentInfo = this.get('tabletDevicePaymentInfo');
          paymentTransactionModel.set('request.card.deviceEncryptedCardInfo', tabletDevicePaymentInfo);
          paymentTransactionModel.set('cardPresent', true);
        } else if (paymentMethod === PAYMENT_METHODS.paymentProcessingDevice) {
          paymentTransactionModel.set('request.paymentDeviceId', this.get('paymentDeviceId'));
        } else if (paymentMethod === PAYMENT_METHODS.creditCard) {
          var cardNumberLast4 = this.calculateCreditCardNumberLast4();
          paymentTransactionModel.set('request.card', {
            cardNumberLast4: cardNumberLast4,
            expirationDate: this.calculateExpirationYearMonth(),
            cardType: this.get('cardData.type'),
            encryptedCardInfo: this.get('cardData.encryptedCardInfo')
          });
        } else if (paymentMethod === PAYMENT_METHODS.ach) {
          var accountNumberLast4 = this.calculateAchAccountNumberLast4();
          var ach = _.clone(this.get('achData'));
          paymentTransactionModel.set('request.ach', ach, {
            accountNumberLast4: accountNumberLast4
          });
        } else if (PAYMENT_METHODS.isStoredPaymentMethod(paymentMethod)) {
          // if it is a stored payment method then the payment method is the vecna id of the stored payment method.
          paymentTransactionModel.set('request.storedPaymentMethodVecnaId', paymentMethod);
        }
      }
    }
  },

  /**
   * Information on different kinds of credit cards:
   * - regular expression constraint for account number
   * - number of digits in payment card security code.
   * @property CARDS
   */
  CARDS: CARDS,

  /**
   * Information on different kinds of payment details type
   *
   * @property PAYMENT_DETAILS_TYPE
   */
   PAYMENT_DETAILS_TYPE: PAYMENT_DETAILS_TYPE,

   /**
    * Information on different kinds of payment methods
    *
    * @property PAYMENT_METHODS
    */
   PAYMENT_METHODS: PAYMENT_METHODS,

  /**
   * Sets up the form model with the needed helper methods from PaymentDetailsModel
   * @method initialize
   * @param attributes {Object} the model attributes
   * @param options {Object} properties related to this model
   *  @param options.isMakingPayment {Boolean} whether this model is used for making a payment or not
   */
  initialize: function(attributes, options) {
    options = options || {};
    this._isMakingPayment = options.isMakingPayment;
    this._patientVecnaId = options.patientVecnaId;
    this._appointmentExternalId = options.appointmentExternalId;

    this.set('billPaymentDetails', {});
    this.set('detailsTypes', [PAYMENT_DETAILS_TYPE.OUTSTANDING_BILL, PAYMENT_DETAILS_TYPE.INSURANCE_ESTIMATION, PAYMENT_DETAILS_TYPE.APPOINTMENT_ESTIMATE]);
    _.bindAll(this, 'save', 'push', 'removeStorePayment', 'preparePaymentData', 'saveTransaction',
                    '_shouldValidateCardData', '_shouldValidateAchData');
  },

  /**
   * Sets the bill message collection which is useful for doing lookups
   * @param billMessageCollection
   */
  setBillMessageCollection: function(billMessageCollection) {
    this._billMessageCollection = billMessageCollection;
  },

  /**
   * Sets the insurance estimation collection which is useful for doing lookups
   * @param insuranceEstimationCollection
   */
  setInsuranceEstimationCollection: function(insuranceEstimationCollection) {
    this._insuranceEstimationCollection = insuranceEstimationCollection;
  },

  /**
   * @method getTotalPaymentAmount
   * @return the total of all selected payments.
   */
  getTotalPaymentAmount: function() {
    var billPaymentDetails = this.getToBePaidBills();
    return _.reduce(billPaymentDetails, function(totalPaymentAmount, billPaymentDetail) {
      return totalPaymentAmount + Number(billPaymentDetail.amount);
    }, 0);
  },

  /**
   * Looks through the credit card type objects and returns the object for the
   * credit card type with the specified name. If no matching card type is
   * found, returns the unknown card object.
   *
   * @method getCardTypeInfoByName
   * @param cardTypeName {String} the name of the credit card type to get information for.
   */
  getCardTypeInfoByName: function(cardTypeName) {
    var cardInfo;
    if (cardTypeName) {
      cardInfo = this.CARDS[cardTypeName.toLowerCase()];
    }
    return cardInfo ? cardInfo : this.CARDS.unknown;
  },

  /**
   * Finds a credit card type by looking at the credit card number
   * @return {Object} the card type
   * @param [number] {String} credit card number. If null, it will look at the field: cardData.cardNumber
   * @method getCardTypeByNumber
   */
  getCardTypeByNumber: function(number) {
    number = number || this.get('cardData.cardNumber');
    return _.find(this.CARDS, function(card) {
      return card.regex.test(number);
    });
  },

  /**
   * Format the amount field to a string with 2 decimal places
   * @param identifier {String} the index of the details object (format id-xxxxxx)
   * @method formatAmount
   */
  formatAmount: function(identifier) {
    var amountFieldName = 'billPaymentDetails[' + identifier + '].amount',
        amount = this.get(amountFieldName);
    if (amount === 0 || amount === '') {
      amount = '';
    } else {
      amount = handlebars.helpers.toFixed(amount, 2);
    }
    this.set(amountFieldName, amount);
  },

  /**
   * @method setExistingStoredPaymentNicknames
   * @param storedPaymentMethodService {StoredPaymentMethodService} stored payment method service used to get all
   *                                                                nicknames for payment methods for the current patient.
   */
  setStoredPaymentMethodService: function(storedPaymentMethodService) {
    this._storedPaymentMethodService = storedPaymentMethodService;
  },

  /**
   * @method hasToBePaidBills
   * @returns {Boolean} whether there are payable bills with amount greater than 0.01
   */
  hasToBePaidBills: function() {
    return !_.isEmpty(this.getToBePaidBills());
  },

  /**
   * @method getToBePaidBills
   * @returns {Object} payable bills with amount greater than 0.01
   */

  getToBePaidBills: function() {
    var billPaymentDetails = this.get('billPaymentDetails');
    return _.pick(billPaymentDetails, function(billPaymentDetail) {
      var finitePaymentAmount = _.isFinite(billPaymentDetail.amount);
      var paymentAmountNotZero = billPaymentDetail.amount >= 0.01;
      var paymentMade = billPaymentDetail.paid;

      return finitePaymentAmount && paymentAmountNotZero && !paymentMade;
    });
  },

  /**
   * Set the newPayment property to true to mark the
   * @param newPaymentTransactionModel {PaymentTransactionModel} the transaction model containing the bill info to mark paid.
   */
  markBillsPaid: function(newPaymentTransactionModel) {
    var billInfoFromTransaction = newPaymentTransactionModel.get('billInfo');
    var vecnaIdsOfPaidBills = this._getBillIdentifiersByExternalIdOrAccountNumber(billInfoFromTransaction);

    var thisPaymentDetailsFormModel = this;
    var billPaymentDetails = this.get('billPaymentDetails');
    var newBillPaymentDetails = _.mapObject(billPaymentDetails, function(billPaymentDetail, billPaymentDetailIdentifier) {
      var billPaymentVecnaId = thisPaymentDetailsFormModel.getBillMessageVecnaIdOrAccountNumberFromIdentifier(billPaymentDetailIdentifier);
      var clonedBillPaymentDetail = _.clone(billPaymentDetail);
      if (_.contains(vecnaIdsOfPaidBills, billPaymentVecnaId)) {
        clonedBillPaymentDetail.paid = true;
      }
      return clonedBillPaymentDetail;
    });
    this.set('billPaymentDetails', newBillPaymentDetails);
    this.trigger('billPaid');
  },

  /**
   * @method getFirstToBePaidBillPaymentDetails
   * @return {Object} - the payment data for the first bill to be paid.
   */
  getFirstToBePaidBillPaymentDetails: function() {
    var toBePaidBills = this.getToBePaidBills();
    if (_.isEmpty(toBePaidBills)) {
      return undefined;
    }

    var thisPaymentDetailsFormModel = this;
    var allToBePaidBillIdentifiers = _.keys(toBePaidBills);
    var allToBePaidBillMessageVecnaIdsOrAccountNumbers = _.map(allToBePaidBillIdentifiers, function(toBePaidBillIdentifier) {
      return thisPaymentDetailsFormModel.getBillMessageVecnaIdOrAccountNumberFromIdentifier(toBePaidBillIdentifier);
    });
    var firstToBePaidBillMessageModel;
    var firstToBePaidEstimateModel;
    if (this._billMessageCollection) {
      firstToBePaidBillMessageModel = this._billMessageCollection.find(function(billMessageModel) {
        return _.contains(allToBePaidBillMessageVecnaIdsOrAccountNumbers, billMessageModel.get('vecnaId'));
      });
    }

    if (this._insuranceEstimationCollection) {
      firstToBePaidEstimateModel = this._insuranceEstimationCollection.find(function(insuranceEstimationModel) {
        return _.contains(allToBePaidBillMessageVecnaIdsOrAccountNumbers, insuranceEstimationModel.get('accountNumber'));
      });
    }

    var firstToBePaidBillIdentifier;
    if (firstToBePaidBillMessageModel != null) {
      firstToBePaidBillIdentifier = firstToBePaidBillMessageModel.getIdentifier();
    } else {
      firstToBePaidBillIdentifier = firstToBePaidEstimateModel.getIdentifier();
    }
    var firstToBePaidBill = _.clone(toBePaidBills[firstToBePaidBillIdentifier]);
    firstToBePaidBill.identifier = firstToBePaidBillIdentifier;
    return firstToBePaidBill;
  },

  /**
   * @method getAllToBePaidBillPaymentDetails
   * @return {Object[]} - array of all the bills to be processed with vecnaId as a property instead of a key.
   */
  getAllToBePaidBillPaymentDetails: function() {
    var toBePaidBills = this.getToBePaidBills();
    var allIdentifierAndPaymentDetailPairs = _.pairs(toBePaidBills);
    return _.map(allIdentifierAndPaymentDetailPairs, function(identifierAndPaymentDetailPair) {
      var identifier = identifierAndPaymentDetailPair[0];
      var paymentDetail = identifierAndPaymentDetailPair[1];
      paymentDetail.identifier = identifier;
      return paymentDetail;
    });
  },

  /**
   * @method getCurrentBillPaymentDetails
   * @return {Object[]} an array of all of the bill payment details that should be paid in this transaction.
   */
  getCurrentBillPaymentDetails: function() {
    var separatePaymentRequestsPerAccount = billingConfigModel.get('separatePaymentRequestsPerAccount');
    var billsToPay = [];
    if (separatePaymentRequestsPerAccount) {
      var firstBillToPay = this.getFirstToBePaidBillPaymentDetails();
      if (firstBillToPay) {
        billsToPay.push(firstBillToPay);
      }
    } else {
      billsToPay = this.getAllToBePaidBillPaymentDetails();
    }
    return billsToPay;
  },

  /**
   * Remove the store payment info from this model.
   * @method removeStorePayment
   */
  removeStorePayment: function() {
    this.unset('storePayment');
  },

  /**
   * Get the last 4 of the credit card number checking both the last4 and cardNumber fields.
   * @method calculateCreditCardNumberLast4
   * @return {String} the last 4 digits of the card number.
   */
  calculateCreditCardNumberLast4: function () {
    var cardNumberLast4 = this.get('cardData.cardNumber');
    if (cardNumberLast4 && cardNumberLast4.length > 4) {
      cardNumberLast4 = cardNumberLast4.substr(cardNumberLast4.length - 4);
      this.set('cardData.cardNumberLast4', cardNumberLast4);
    }
    return cardNumberLast4;
  },

  /**
   * Get the last 4 of the ACH Account number checking both the last4 and accountNumber fields.
   * @method calculateAchAccountNumberLast4
   * @return {String} the last 4 digits of the ACH Account number.
   */
  calculateAchAccountNumberLast4: function () {
    var achAccountNumberLast4 = this.get('achData.accountNumber');
    if (achAccountNumberLast4 && achAccountNumberLast4.length > 4) {
      achAccountNumberLast4 = achAccountNumberLast4.substr(achAccountNumberLast4.length - 4);
      this.set('achData.accountNumberLast4', achAccountNumberLast4);
    }
    return achAccountNumberLast4;
  },

  /**
   * Fall back to the already calculated value if cleared, otherwise re-calculate it.
   * @method calculateExpirationYearMonth
   * @return {String[]} the year then month as an array (i.e. [2020, 5] for May 2020).
   */
  calculateExpirationYearMonth: function () {
    var expirationYearString = this.get('cardData.expirationYear');
    var expirationMonthString = this.get('cardData.expirationMonth');
    var expirationYearMonthStringArray = expirationYearString && expirationMonthString && [expirationYearString, expirationMonthString];
    var expirationYearMonth = dateUtils.convertYearMonthArrayToNumericArray(expirationYearMonthStringArray);

    if (!expirationYearMonth) {
      expirationYearMonth = this.get('cardData.expirationYearMonth');
    } else {
      this.set('cardData.expirationYearMonth', expirationYearMonth);
    }
    return expirationYearMonth;
  },

  /**
   * Prepare the current payment transaction model and copy sensitive data over to it so we can clear the sensitive
   * data from the form (and form model)
   * @method preparePaymentData
   */
  preparePaymentData: function() {
    var thisPaymentDetailsFormModel = this;
    var lastSyncedPaymentTransactionModel = this.getCurrentPaymentTransactionModel();
    var hasNotAlreadyValidatedRequest = !lastSyncedPaymentTransactionModel;
    if (hasNotAlreadyValidatedRequest) {
      var errors = this.validate();

      if (!_.isEmpty(errors)) {
        return $.Deferred().reject(errors).promise();
      }
    }

    var newPaymentTransactionModel = this._getNewPaymentTransactionModel();

    this.trackModel('paymentTransaction', newPaymentTransactionModel);
    thisPaymentDetailsFormModel.setCurrentPaymentTransactionModel(newPaymentTransactionModel);
    return billingConfigModel.loadOnce()
      .then(function() {
        thisPaymentDetailsFormModel.push();
        thisPaymentDetailsFormModel.untrackModel('paymentTransaction');
        return newPaymentTransactionModel;
      });
  },

  /**
   * Save the current payment transaction to the server.
   * @method saveTransaction
   */
  saveTransaction: function() {
    var paymentTransactionModel = this.getCurrentPaymentTransactionModel();
    return billingConfigModel.loadOnce()
      .then(function() {
        return paymentTransactionModel.save();
      });
  },

  /**
   * @method getCurrentPaymentTransactionModel
   * @return {PaymentTransactionModel} the current payment transaction model ready to send to the server.
   */
  getCurrentPaymentTransactionModel: function() {
    return this._paymentTransactionModel;
  },

  /**
   * @method setCurrentPaymentTransactionModel
   * @param paymentTransactionModel {PaymentTransactionModel} the payment transaction model to use
   */
  setCurrentPaymentTransactionModel: function(paymentTransactionModel) {
    this._paymentTransactionModel = paymentTransactionModel;
  },

  /**
   * Clear any sensitive data card or ach data from the form model and makes sure non-sensitive values are cached to
   * send with the payment transaction.
   * @method clearSensitivePaymentData
   */
  clearSensitivePaymentData: function() {
    // All credit card data.
    this.unset('cardData');
    // All ach data.
    this.unset('achData');
    this.unset('confirmAccountNumber');
    // Store payment
    this.removeStorePayment();
  },

  /**
   * Clears all card and ach data.  Including anything saved in the existing payment transaction model.
   */
  clearAllPaymentMethodData: function() {
    this.clearSensitivePaymentData();
    delete this._paymentTransactionModel;
  },

  /**
   * Create a new payment transaction model from data known by the view.
   * @method _getNewPaymentTransactionModel
   * @return {PaymentTransactionModel} a new payment transaction model seeded with stuff from the view's context.
   * @private
   */
  _getNewPaymentTransactionModel: function () {
    // Set stuff on the transaction model that isn't included in the form model.
    return new PaymentTransactionModel({
      usernameOfRequestor: userService.currentUserModel.get('username'),
      patientVecnaId: this._patientVecnaId,
      cardPresent: false
    });
  },

  /**
   * @method _shouldValidatePaymentDevice
   * @returns {Boolean} true if the paymentProcessingDevice should be validated, false otherwise.
   * @private
   */
  _shouldValidatePaymentDevice: function() {
    return this.get('paymentMethod') === PAYMENT_METHODS.paymentProcessingDevice;
  },

  /**
   * @method _shouldValidateCardData
   * @returns {Boolean} true if the card data should be validated, false otherwise.
   * @private
   */
  _shouldValidateCardData: function() {
    return this.get('paymentMethod') === PAYMENT_METHODS.creditCard;
  },

  /**
   * @method _shouldValidateSensitiveCardData
   * @returns {Boolean} true if the sensitive card data should be validated, false otherwise.
   * @private
   */
  _shouldValidateSensitiveCardData: function() {
    return this._shouldValidateCardData() && !this.get('cardData.encryptedCardInfo');
  },

  /**
   * @method _shouldValidateAchData
   * @returns {Boolean} true if the ach data should be validated (payment method is ach and ach isn't saved), false otherwise.
   * @private
   */
  _shouldValidateAchData: function() {
    return this.get('paymentMethod') === PAYMENT_METHODS.ach;
  },

  /**
   * @returns {Boolean} true if the nickname should be validated (either saving the payment method or creating a stored payment method).
   * @private
   */
  _shouldValidateNickname: function() {
    return this.get('isStoredPayment') || this.get('storePayment.save');
  },

  /**
   * @method _shouldValidateCardOrAchData
   * @returns {Boolean} true if the card data or ach data should be validated, false otherwise.
   * @private
   */
  _shouldValidateCardOrAchData: function() {
    return this._shouldValidateAchData() || this._shouldValidateCardData();
  },

  /**
   * @method _getExistingStoredPaymentNicknames
   * @return {String[]} the existing stored payment nicknames to validate against.
   * @private
   */
  _getExistingStoredPaymentNicknames: function() {
    return this._storedPaymentMethodService.getAllStoredPaymentMethodNicknamesForPatient();
  },

  /**
   * Take the bills to pay and the collection of bill messages and generate the bill details for the payment transaction.
   * @method _getBillInfoForPaymentTransaction
   * @param billPaymentDetails {Object[]} bills to pay with vecnaId and amount values.
   * @return {Object[]} amount, accountNumber and external id of each bill being paid.
   * @private
   */
  _getBillInfoForPaymentTransaction: function(billPaymentDetails) {
    var paymentDetailsFormModel = this;
    return _.map(billPaymentDetails, function(billPaymentDetail) {
      var billMessageCollection = paymentDetailsFormModel._billMessageCollection;
      var billMessageVecnaIdOrAccountNumber = paymentDetailsFormModel.getBillMessageVecnaIdOrAccountNumberFromIdentifier(billPaymentDetail.identifier);
      var billMessageModel = billMessageCollection && billMessageCollection.getMessageByVecnaId(billMessageVecnaIdOrAccountNumber);

      var insuranceEstimationCollection = paymentDetailsFormModel._insuranceEstimationCollection;
      var insuranceEstimationModel = insuranceEstimationCollection && insuranceEstimationCollection.getByAccountNumber(billMessageVecnaIdOrAccountNumber);
      var accountNumber = billMessageModel != null ? billMessageModel.get('billDTO.accountNumber') : insuranceEstimationModel.get('accountNumber');
      var billExternalId = billMessageModel != null ? billMessageModel.get('billDTO.externalBill.externalId') : null;
      var billVecnaId = billMessageModel != null ? billMessageModel.get('billDTO.vecnaId') : null;
      var amountDue = billMessageModel != null ? billMessageModel.get('billDTO.externalBill.totalCharges') : null;
      return {
        amount: parseFloat(billPaymentDetail.amount) || 0,
        amountDue: amountDue,
        accountNumber: accountNumber,
        billExternalId: billExternalId,
        billVecnaId: billVecnaId,
        appointmentExternalId: paymentDetailsFormModel._appointmentExternalId
      };
    });
  },

  /**
   * Get total balance of unpaid estimates
   * @method getTotalEstimatesUnpaidDue
   * @return {Number} Total balance of unpaid estimates for active patient.
   */
  getTotalEstimatesUnpaidDue: function() {
    var paymentDetailsFormModel = this;
    if (this._insuranceEstimationCollection) {
      return this._insuranceEstimationCollection.reduce(function(amountSum, insuranceEstimationModel) {
        var identifier = insuranceEstimationModel.getIdentifier();
        var hasUnapprovedInsuranceUpdates = insuranceEstimationModel.get('hasUnapprovedInsuranceUpdates');
        var isPaid = paymentDetailsFormModel.get('billPaymentDetails.' + identifier + '.paid');
        if (!isPaid && !hasUnapprovedInsuranceUpdates) {
          var itemRemainingBalance = insuranceEstimationModel.get('remainingBalance');
          itemRemainingBalance = itemRemainingBalance || 0;
          amountSum += Number(itemRemainingBalance);
        }
        return amountSum;
      }, 0);
    } else {
      return 0;
    }
  },

  /**
   * Get total balance of unpaid bills
   * @method getTotalBillsUnpaidDue
   * @return {Number} Total balance of unpaid bills for active patient.
   */
  getTotalBillsUnpaidDue: function(){
    var paymentDetailsFormModel = this;
    if (this._billMessageCollection) {
      return this._billMessageCollection.reduce(function(amountSum, secureMessageModel){
        var identifier = secureMessageModel.getIdentifier();
        var isPaid = paymentDetailsFormModel.get('billPaymentDetails.' + identifier + '.paid');
        var typeIsBill = secureMessageModel.get('type') === 'BILL';

        if(typeIsBill && !isPaid){
          var itemAccountBalance = secureMessageModel.get('billDTO.externalBill.accountBalance');
          itemAccountBalance = itemAccountBalance || 0;
          amountSum += Number(itemAccountBalance);
        }
        return amountSum;
      }, 0);
    } else {
      return 0;
    }
  },

  /**
   * Set payment amount to 0 for bill.
   * @method payNone
   * @public
   * @param billMessageModel {Object} billMessageModel to get vecnaId
   */
  payNone: function(billMessageModel) {
    var identifier = billMessageModel.getIdentifier();
    this.set('billPaymentDetails.' + identifier + '.amount', '0.00');
  },

  /**
   * Set payment amount to 0 for estimate.
   * @method payNoneEstimate
   * @public
   * @param estimateModel {Object} estimateModel to get accountNumber
   */
  payNoneEstimate: function(estimateModel) {
    var identifier = estimateModel.getIdentifier();
    this.set('billPaymentDetails.' + identifier + '.amount', '0.00');
  },

  /**
   * Set payment amount to amount due for bill.
   * @method payFull
   * @public
   * @param billMessageModel {Object} billMessageModel to get accountBalance and vecnaId
   */
  payFull: function(billMessageModel) {
    var amount = billMessageModel.get('billDTO.externalBill.accountBalance');
    amount = handlebars.helpers.toFixed(amount, 2);
    var identifier = billMessageModel.getIdentifier();
    this.set('billPaymentDetails.' + identifier + '.amount', amount);
  },

  /**
   * Set payment amount to amount due for estimate.
   * @method payFullEstimate
   * @public
   * @param estimateModel {Object} estimateModel to get remainingBalance and accountNumber
   */
  payFullEstimate: function(estimateModel) {
    var amount = estimateModel.get('remainingBalance');
    amount = handlebars.helpers.toFixed(amount, 2);
    var identifier = estimateModel.getIdentifier();
    this.set('billPaymentDetails.' + identifier + '.amount', amount);
  },

  /**
   * Pay all outstanding bills.
   * @method payAllBills
   * @public
   */
  payAllBills: function() {
    this._billMessageCollection.each(this.payFull, this);
  },

  /**
   * Compares the servicesId with the id of the selected bill.
   * When they match, runs _payFull on item.
   * @method payFullByBillId
   */
  payFullByBillId: function(billId) {
    var matchesSelectedBill = this._billMessageCollection.get(billId);
    if(matchesSelectedBill) {
      this.payFull(matchesSelectedBill);
    }
  },

  /**
   * Convert an array of billInfo objects from a payment transaction to an array of bill message vecna ids.
   * @method _getBillIdentifiersByExternalIdOrAccountNumber
   * @param billInfos {Object} billInfos to get billIdentifiers
   * @private
   */
  _getBillIdentifiersByExternalIdOrAccountNumber: function(billInfos) {
    return _.map(billInfos, function(billInfo) {
      var billMessageModel = this._billMessageCollection && this._billMessageCollection.find(function(billMessageModel) {
        return billInfo.billExternalId === billMessageModel.get('billDTO.externalBill.externalId');
      });
      var insuranceEstimationModel = this._insuranceEstimationCollection && this._insuranceEstimationCollection.find(function(insuranceEstimationModel) {
        return billInfo.accountNumber === insuranceEstimationModel.get('accountNumber');
      });

      return billMessageModel != null ? billMessageModel.get('vecnaId') : insuranceEstimationModel.get('accountNumber');
    }, this);
  },

  /**
   * @method getBillMessageVecnaIdOrAccountNumberFromIdentifier
   * @param identifier the identifier for the bill message or account number in id-{vecnaId} or id-{accountNumber} format.
   * @returns {string} the bill message vecnaId or account number
   */
  getBillMessageVecnaIdOrAccountNumberFromIdentifier: function(identifier) {
    return identifier.substring(3);
  }
});

PaymentDetailsFormModel.PAYMENT_METHODS = PAYMENT_METHODS;
PaymentDetailsFormModel.PAYMENT_DETAILS_TYPE = PAYMENT_DETAILS_TYPE;
PaymentDetailsFormModel.CARDS = CARDS;

module.exports = PaymentDetailsFormModel;
